import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import { ApiGet, ApiDelete, BaseURL } from '../../../../ApiSetup/Api';
import axios from 'axios';

const WebappUpload = () => {
  const dispatch = useDispatch();
  
  const [status, setStatus] = useState({
    isDeployed: false,
    deployInfo: null,
    loading: true,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setStatus(prev => ({ ...prev, loading: true }));
      const response = await ApiGet('admin/webapp/status');
      if (response?.data?.status === 200) {
        setStatus({
          isDeployed: response.data.data.isDeployed,
          deployInfo: response.data.data.deployInfo,
          loading: false,
        });
      }
    } catch (error) {
      console.error('Error fetching webapp status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error('Please select a .zip file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('webapp', selectedFile);

    try {
      const response = await axios.post(
        `${BaseURL}admin/webapp/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('auth_token'),
          },
          timeout: 300000, // 5 minutes timeout for large file uploads
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );

      if (response?.data?.status === 200) {
        toast.success(response.data.message);
        setSelectedFile(null);
        fetchStatus();
      } else {
        toast.error(response?.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading webapp:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload webapp');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the deployed webapp?')) {
      return;
    }

    dispatch(showLoader(true));
    try {
      const response = await ApiDelete('admin/webapp/delete');
      if (response?.data?.status === 200) {
        toast.success(response.data.message);
        fetchStatus();
      } else {
        toast.error(response?.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting webapp:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete webapp');
    } finally {
      dispatch(showLoader(false));
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const webappUrl = `${BaseURL}webapp`;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>
            Flutter Web App Deployment
          </Typography>
          <IconButton onClick={fetchStatus} disabled={status.loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Grid>

      {/* Status Card */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">Deployment Status</Typography>
            {status.loading ? (
              <CircularProgress size={20} />
            ) : status.isDeployed ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Deployed"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<ErrorIcon />}
                label="Not Deployed"
                color="warning"
                size="small"
              />
            )}
          </Box>

          {status.isDeployed && status.deployInfo && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last Modified: {formatDate(status.deployInfo.lastModified)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Size: {formatBytes(status.deployInfo.size)}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(webappUrl, '_blank')}
                  size="small"
                >
                  Open Web App
                </Button>
              </Box>
            </Box>
          )}

          {!status.isDeployed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No Flutter web app is currently deployed. Upload a zip file containing your Flutter web build to get started.
            </Alert>
          )}
        </Paper>
      </Grid>

      {/* Upload Section */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Upload Flutter Web Build
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Instructions:</strong>
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Run <code>flutter build web --release</code> in your Flutter project</li>
              <li>Navigate to <code>build/web</code> folder</li>
              <li>Zip the contents of the <code>web</code> folder (or the folder itself)</li>
              <li>Upload the zip file below</li>
            </ol>
          </Alert>

          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: selectedFile ? '#f0f9ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: '#3E4094',
                backgroundColor: '#f5f5ff',
              },
            }}
            onClick={() => document.getElementById('webapp-file-input').click()}
          >
            <input
              id="webapp-file-input"
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: '#3E4094', mb: 1 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {selectedFile ? selectedFile.name : 'Click to select or drag and drop'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedFile
                ? `Size: ${formatBytes(selectedFile.size)}`
                : 'Only .zip files are accepted (max 100MB)'}
            </Typography>
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              sx={{
                backgroundColor: '#3E4094',
                '&:hover': { backgroundColor: '#363785' },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload & Deploy'}
            </Button>

            {selectedFile && (
              <Button
                variant="outlined"
                onClick={() => setSelectedFile(null)}
                disabled={uploading}
              >
                Clear
              </Button>
            )}

            {status.isDeployed && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={uploading}
              >
                Delete Webapp
              </Button>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* URL Info */}
      {status.isDeployed && (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Access URL
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              {webappUrl}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Share this URL to access the Flutter web application.
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default WebappUpload;
